<template>
  <StepsCard :step="currentStep">
    <template #step>Step {{ currentStep }} of {{ totalSteps }}</template>
    <template #body>
      <div class="modal_heading-text">
        <h4>{{ $t('login.headingText') }}</h4>
      </div>
      <div class="modal_caption">
        <p>
          {{ $t('login.caption') }}
        </p>
      </div>
      <div class="modal_text-input-container">
        <div class="modal_input-container">
          <Input
            aria-label="Email"
            label="Email address"
            type="text"
            v-model="userInput.email"
            name="email"
            :rules="rules.email"
          />
        </div>
        <div class="modal_input-container">
          <Input
            aria-label="Password"
            label="Password"
            type="password"
            v-model="userInput.password"
            name="password"
            :rules="rules.password"
          />
        </div>
      </div>
      <div class="modal_button-container">
        <div>
          <Button
            text="Next"
            :radius="10"
            @buttonClicked="buttonClicked()"
            :disabled="isButtonDisabled"
          />
        </div>
        <div class="modal_error-container">
          <span class="error-text" v-if="error">
            {{ error }}
          </span>
        </div>
        <div class="modal_small-text">
          <small>{{ $t('login.passwordReset') }} </small>
          <Popper
            arrow
            content="Contact your administrator to reset password"
            :hover="true"
            class="tooltip"
          >
            <font-awesome-icon :icon="['fa', 'circle-info']" />
          </Popper>
        </div>
      </div>
    </template>
  </StepsCard>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from 'vue';
import { Button, Input, StepsCard } from '@yellowcard/components-library-vue3';
import { useAuthStore } from '@/store/modules/auth';
import { PortalServiceTypes } from '@/types/portalService';
import validator from 'validator';
import { storeToRefs } from 'pinia';
import router from '@/router';
import Popper from 'vue3-popper';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default defineComponent({
  name: 'LoginModal',
  components: {
    Button,
    Input,
    StepsCard,
    Popper,
    FontAwesomeIcon,
  },
  setup() {
    const error = ref(undefined);
    const isButtonDisabled = ref(true);
    const authStore = useAuthStore();
    const { user, temporaryUserLogin, currentStep, totalSteps, challenge } =
      storeToRefs(authStore);
    const userInput = reactive({
      email: '',
      password: '',
    });

    const rules = computed(() => ({
      email: (value: string) => {
        if (validator.isEmpty(value)) return 'Your email address is required';
        if (!validator.isEmail(value))
          return 'Please provide a valid email address';
        return true;
      },
      password: (value: string) => {
        if (validator.isEmpty(value)) return 'Your password is required';
        return true;
      },
    }));

    const buttonClicked = async () => {
      isButtonDisabled.value = true;
      error.value = undefined;
      try {
        await authStore.loginUser(userInput.email, userInput.password);
        if (
          challenge.value ===
          PortalServiceTypes.ChallengeName.PASSWORD_RESET_REQUESTED
        ) {
          await router.push('requested-password-reset');
        } else if (
          temporaryUserLogin.value &&
          temporaryUserLogin.value.challengeName ===
            PortalServiceTypes.ChallengeName.NEW_PASSWORD_REQUIRED
        ) {
          currentStep.value += 1;
          totalSteps.value += 1;
          await router.push('reset-password');
        } else {
          authStore.setIsLoggedIn(true);
          currentStep.value += 1;
          await router.push('two-factor-authentication');
        }
      } catch (e: any) {
        isButtonDisabled.value = false;
        error.value = e.message;
      }
    };

    watch(userInput, newInput => {
      isButtonDisabled.value = !(
        rules.value.email(newInput.email) === true &&
        rules.value.password(newInput.password) === true
      );
    });

    return {
      error,
      userInput,
      rules,
      buttonClicked,
      isButtonDisabled,
      authStore,
      user,
      currentStep,
      totalSteps,
    };
  },
});
</script>

<style lang="scss">
@import '@/common/styles/_base.scss';
@import '@/common/styles/_text.scss';

.error-text {
  top: 451px;
  font-style: normal;
  font-weight: 400;
  font-size: $text-size-small-text;
  line-height: 18px;
  color: #b00020;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
}

input[type='password'] {
  font-size: 17px !important;
}

.tooltip {
  --popper-theme-background-color: #333333;
  --popper-theme-background-color-hover: #333333;
  --popper-theme-text-color: #ffffff;
  --popper-theme-border-width: 0px;
  --popper-theme-border-style: solid;
  --popper-theme-border-radius: 6px;
  --popper-theme-padding: 20px;
  --popper-theme-box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
}
</style>
